import { create } from 'zustand';
import { Message, ConversationStatus } from '@/types/conversation';
import { nanoid } from 'nanoid';

/**
 * ChatState - 对话状态接口定义
 * 用于管理聊天界面的消息列表、生成状态以及 AI 的运行状态。
 */
interface ChatState {
  // --- 状态数据 ---
  /** 当前对话的所有消息列表 */
  messages: Message[];
  /** 标志位：AI 是否正在生成回复中 */
  isGenerating: boolean;
  /** AI 运行状态（包含 Token 数、当前步骤描述等信息） */
  status: ConversationStatus | null;
  /** 标志位：预览页面是否已准备就绪 */
  isPreviewReady: boolean;
  /** 标志位：构建是否失败 */
  isBuildFailed: boolean;
  /** 构建失败的原因 */
  buildFailReason: string | null;
  /** 消息列表的最大容量限制，超出后将裁切旧消息 */
  maxMessages: number;
  /** 已经过的时间（秒） */
  elapsedSeconds: number;
  /** 最后一次追加的进度消息索引 */
  lastProgressIndex: number;

  // --- 基础 Actions ---
  /**
   * 设置最后一次追加的进度消息索引
   * @param index - 索引值
   */
  setLastProgressIndex: (index: number) => void;
  /**
   * 设置已经过的时间
   * @param val - 秒数或更新函数
   */
  setElapsedSeconds: (val: number | ((prev: number) => number)) => void;
  /**
   * 设置 AI 生成状态
   * @param val - 是否正在生成
   */
  setIsGenerating: (val: boolean) => void;
  /**
   * 设置预览页面就绪状态
   * @param val - 是否已就绪
   */
  setIsPreviewReady: (val: boolean) => void;
  /**
   * 设置构建错误状态
   * @param isFailed - 是否失败
   * @param reason - 失败原因
   */
  setBuildError: (isFailed: boolean, reason?: string | null) => void;
  /**
   * 设置当前的 AI 运行状态
   * @param status - 状态对象或 null
   */
  setStatus: (status: ConversationStatus | null) => void;
  /**
   * 清除所有消息并重置相关状态
   */
  clearMessages: () => void;

  // --- 消息操作逻辑 ---
  /**
   * 追加单条消息到列表末尾
   * @param message - 消息的部分属性，缺失部分将自动补充默认值
   */
  appendMessage: (message: Partial<Message>) => void;
  /**
   * 批量追加多条消息（常用于加载历史记录）
   * @param messages - 消息对象数组
   */
  appendMessagesBatch: (messages: Partial<Message>[]) => void;
  /**
   * 根据消息 ID 更新指定消息的内容
   * @param id - 消息的唯一标识符
   * @param updater - 更新函数，接收旧消息并返回新消息
   */
  updateMessageById: (id: string, updater: (msg: Message) => Message) => void;

  // --- 内部工具 ---
  /**
   * 强制执行消息数量限制，防止内容过多导致性能问题或内存溢出
   * @param list - 待检查的消息列表
   * @returns 裁切后的消息列表
   */
  enforceMessageLimit: (list: Message[]) => Message[];
}

/**
 * useChatStore - 对话业务状态中心 (Zustand)
 * 负责管理消息列表、会话状态及相关的原子操作。
 * 提供流式更新支持，并具备消息长度自动裁切功能。
 */
export const useChatStore = create<ChatState>((set, get) => ({
  /** 初始消息列表为空 */
  messages: [],
  /** 初始生成状态为 false */
  isGenerating: false,
  /** 初始预览就绪状态为 false */
  isPreviewReady: false,
  /** 初始构建失败状态为 false */
  isBuildFailed: false,
  /** 初始构建失败原因为 null */
  buildFailReason: null,
  /** 初始 AI 运行状态为 null */
  status: null,
  /** 已经过的时间（秒） */
  elapsedSeconds: 0,
  /** 默认保留最近 100 条消息 */
  maxMessages: 100,
  /** 初始进度消息索引为 -1 */
  lastProgressIndex: -1,

  /**
   * 设置最后一次追加的进度消息索引
   * @param lastProgressIndex - 索引值
   */
  setLastProgressIndex: (lastProgressIndex) => set({ lastProgressIndex }),

  /**
   * 设置已经过的时间
   * @param val - 秒数或更新函数
   */
  setElapsedSeconds: (val) => {
    if (typeof val === 'function') {
      set((state) => ({ elapsedSeconds: val(state.elapsedSeconds) }));
    } else {
      set({ elapsedSeconds: val });
    }
  },

  /**
   * 设置是否正在生成中
   * @param isGenerating - 布尔值
   */
  setIsGenerating: (isGenerating) => set({ isGenerating }),

  /**
   * 设置预览页面就绪状态
   * @param isPreviewReady - 是否已就绪
   */
  setIsPreviewReady: (isPreviewReady) => set({ isPreviewReady }),

  /**
   * 设置构建错误状态
   * @param isBuildFailed - 是否失败
   * @param buildFailReason - 失败原因
   */
  setBuildError: (isBuildFailed, buildFailReason = null) => set({ isBuildFailed, buildFailReason }),

  /**
   * 设置 AI运行状态信息
   * @param status - 状态详情
   */
  setStatus: (status) => set({ status }),

  /**
   * 重置所有对话状态，包括消息列表、状态和生成标志
   */
  clearMessages: () =>
    set({
      messages: [],
      status: null,
      isGenerating: false,
      isPreviewReady: false,
      isBuildFailed: false,
      buildFailReason: null,
      elapsedSeconds: 0,
      lastProgressIndex: -1,
    }),

  /**
   * 消息长度裁切逻辑实现
   * @param list - 当前消息数组
   * @returns 裁切后的数组
   */
  enforceMessageLimit: (list) => {
    const { maxMessages } = get();
    if (list.length <= maxMessages) return list;
    return list.slice(list.length - maxMessages);
  },

  /**
   * 插入新消息，支持自动生成 ID 和时间戳
   * @param message - 传入的消息片段
   */
  appendMessage: (message) => {
    const now = new Date();
    const fullMessage: Message = {
      id: message.id || nanoid(),
      timestamp: message.timestamp || now,
      content: '',
      role: 'assistant',
      ...message,
    } as Message;

    set((state) => ({
      messages: state.enforceMessageLimit([...state.messages, fullMessage]),
    }));
  },

  /**
   * 批量插入消息，通常在初始化或切换会话时调用
   * @param messageList - 消息对象列表
   */
  appendMessagesBatch: (messageList) => {
    if (messageList.length === 0) return;
    const now = new Date();
    const fullMessages = messageList.map(
      (msg) =>
        ({
          id: msg.id || nanoid(),
          timestamp: msg.timestamp || now,
          content: '',
          role: 'assistant',
          ...msg,
        }) as Message,
    );

    set((state) => ({
      messages: state.enforceMessageLimit([...state.messages, ...fullMessages]),
    }));
  },

  /**
   * 精确更新指定 ID 的消息（流式输出的关键操作）
   * @param id - 消息 ID
   * @param updater - 更新逻辑回调
   */
  updateMessageById: (id, updater) => {
    set((state) => {
      let found = false;
      const nextMessages = state.messages.map((msg) => {
        if (!found && msg.id === id) {
          found = true;
          return updater(msg);
        }
        return msg;
      });

      if (!found) return state; // 性能优化：未找到匹配项时不触发 Store 更新
      return { messages: nextMessages };
    });
  },
}));
